import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { congesApi } from '../../services/conges-api';
import { NavigationMois } from '../../components/ndf/ui/NavigationMois';
import { moisCourant, useMoisDeSession } from '../../hooks/useMoisDeSession';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Case,
  Champ,
  Saisie,
  Section,
} from '../../components/ndf/ui/Champ';
import { MessageErreur } from '../../components/ndf/ui/MessageEtat';
import { erreurGlobale } from '../../components/ndf/ui/erreurs-champs';
import { useToast } from '../../context/ToastContext';
import { periodeFr } from '../../lib/periode-conges';

/**
 * Envoi paie : la saisie assistée MySilae (specs §J, R-PAI-03).
 *
 * C'EST LA VOIE NOMINALE, pas un pis-aller : l'accès API de KPMG n'est pas
 * ouvert. L'écran est donc conçu pour être RECOPIÉ, pas seulement lu — un
 * salarié après l'autre, avec ses absences, ses primes et ses titres, et une
 * case à cocher par ligne pour ne pas perdre le fil.
 *
 * Les lignes « à régulariser en paie » sautent aux yeux : ce sont celles qui
 * portent sur une période déjà transmise, et les oublier coûte un bulletin
 * faux. Seule la RH décoche le marqueur, et l'action est tracée.
 */

export function CongesPaiePage() {
  const qc = useQueryClient();
  const toast = useToast();
  // Le mois survit à un aller-retour vers un autre écran (Lot 7, point 10).
  const [mois, setMois] = useMoisDeSession('paie', moisCourant());
  // Les cases « saisi » ne sont PAS persistées : elles servent à tenir le fil
  // d'une session de recopie, pas à décrire un état de la paie. Le seul
  // marqueur qui engage est « à régulariser », et lui vit en base.
  const [saisis, setSaisis] = useState<Set<string>>(new Set());
  const [nouvellePrime, setNouvellePrime] = useState<null | { salarieId: string }>(null);
  const [libelle, setLibelle] = useState('');
  const [montant, setMontant] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['conges', 'paie', mois],
    queryFn: () => congesApi.tickets.paie(mois),
  });

  const rafraichir = () => qc.invalidateQueries({ queryKey: ['conges', 'paie', mois] });

  const regulariser = useMutation({
    mutationFn: (id: string) => congesApi.tickets.regularise(id),
    onSuccess: () => {
      toast.success('Marqueur retiré, l’action est tracée.');
      rafraichir();
    },
    onError: (e) => toast.error(erreurGlobale(e) ?? 'L’action a échoué.'),
  });

  const creerPrime = useMutation({
    mutationFn: () =>
      congesApi.tickets.creerPrime({
        salarie_id: nouvellePrime!.salarieId,
        mois,
        libelle,
        montant: Number(montant.replace(',', '.')),
      }),
    onSuccess: () => {
      toast.success('Prime enregistrée.');
      setNouvellePrime(null);
      setLibelle('');
      setMontant('');
      rafraichir();
    },
    onError: (e) => toast.error(erreurGlobale(e) ?? 'L’enregistrement a échoué.'),
  });

  const basculer = (id: string) =>
    setSaisis((s) => {
      const copie = new Set(s);
      if (copie.has(id)) copie.delete(id);
      else copie.add(id);
      return copie;
    });

  if (isLoading) return <p className="p-6 text-nuit/60">Chargement…</p>;
  if (error) return <div className="p-6"><MessageErreur erreur={error} /></div>;

  const salaries = data?.salaries ?? [];
  const aRegulariser = salaries.flatMap((s) => s.absences.filter((a) => a.a_regulariser_paie));

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-4 md:p-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-titre text-xl font-bold text-nuit">Envoi paie</h1>
          <p className="text-sm text-nuit/60">
            À recopier dans MySilae, salarié par salarié. Cochez au fur et à mesure.
          </p>
        </div>
        <NavigationMois valeur={mois} onChange={setMois} />
      </header>

      {aRegulariser.length > 0 && (
        <p className="rounded-lg border border-jaune-fond/60 border-l-4 border-l-jaune-fond bg-white p-3 text-sm text-jaune">
          {aRegulariser.length} absence(s) portent le marqueur « à régulariser en paie » : elles
          concernent une période déjà transmise.
        </p>
      )}

      {salaries.map((s) => (
        <Section key={s.id} titre={s.nom} teinte="ciel">
          <dl className="mb-3 flex flex-wrap gap-6 text-sm">
            <div>
              <dt className="text-nuit/60">Titres restaurant</dt>
              <dd className="tabular-nums text-nuit">{s.tickets}</dd>
            </div>
            <div>
              {/* R-PAI-03 : ce qui se recopie dans MySilae, ce sont des JOURS
                  du mois affiché, pas un nombre d'absences. Le compte
                  d'enregistrements donnait « 1 » pour cinq jours de congés. */}
              <dt className="text-nuit/60">Jours d’absence dans le mois</dt>
              <dd className="tabular-nums text-nuit">
                {s.absences.reduce((t, a) => t + a.jours_du_mois, 0)}
              </dd>
            </div>
          </dl>

          {s.absences.length === 0 ? (
            <p className="text-sm text-nuit/60">Aucune absence ce mois-ci.</p>
          ) : (
            <ul className="space-y-1">
              {s.absences.map((a) => (
                <li
                  key={a.id}
                  className={`flex flex-wrap items-center justify-between gap-2 rounded p-2 text-sm ${
                    a.a_regulariser_paie ? 'bg-jaune-fond/20' : ''
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <Case
                      label=""
                      checked={saisis.has(a.id)}
                      onChange={() => basculer(a.id)}
                    />
                    <span className={saisis.has(a.id) ? 'text-nuit/40 line-through' : 'text-nuit'}>
                      {a.type_libelle}, {periodeFr(a)} ({a.jours_du_mois} j ce mois-ci)
                      {a.a_cheval && (
                        <span className="block text-xs text-nuit/60">
                          À cheval sur deux mois, {a.nb_jours} j au total. Ce mois-ci : le{' '}
                          {a.dates_du_mois.map((d) => d.slice(8)).join(', ')}.
                        </span>
                      )}
                    </span>
                    {a.a_posteriori && (
                      <span className="whitespace-nowrap rounded-full bg-jaune-fond/40 px-2 py-0.5 text-xs text-jaune">
                        a posteriori
                      </span>
                    )}
                  </span>
                  {a.a_regulariser_paie && (
                    <BoutonSecondaire
                      type="button"
                      className="!min-h-9"
                      onClick={() => regulariser.mutate(a.id)}
                    >
                      Régularisation faite
                    </BoutonSecondaire>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-3">
            <p className="mb-1 text-sm font-medium text-nuit">Primes</p>
            {s.primes.length === 0 ? (
              <p className="text-sm text-nuit/60">Aucune.</p>
            ) : (
              <ul className="space-y-1 text-sm text-nuit/80">
                {s.primes.map((p) => (
                  <li key={p.id}>
                    {p.libelle} : {p.montant.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                  </li>
                ))}
              </ul>
            )}
            {nouvellePrime?.salarieId === s.id ? (
              <div className="mt-2 flex flex-wrap items-end gap-2">
                <Champ label="Libellé" requis>
                  <Saisie value={libelle} onChange={(e) => setLibelle(e.target.value)} />
                </Champ>
                <Champ label="Montant" requis>
                  <Saisie
                    value={montant}
                    onChange={(e) => setMontant(e.target.value)}
                    inputMode="decimal"
                    className="w-32"
                  />
                </Champ>
                <BoutonSecondaire type="button" onClick={() => setNouvellePrime(null)}>
                  Annuler
                </BoutonSecondaire>
                <BoutonPrincipal
                  type="button"
                  disabled={!libelle.trim() || !montant || creerPrime.isPending}
                  onClick={() => creerPrime.mutate()}
                >
                  Enregistrer
                </BoutonPrincipal>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setNouvellePrime({ salarieId: s.id })}
                className="mt-1 text-xs text-violet underline"
              >
                Ajouter une prime
              </button>
            )}
          </div>
        </Section>
      ))}

      {salaries.length === 0 && (
        <p className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-nuit/60">
          Aucun salarié à afficher.
        </p>
      )}
    </div>
  );
}
